import type { FlagValue, FlagStore, FlagChangeCallback, FlagSource, FlagLoadOptions, FlagEntry } from './types';

export class FeatureFlags {
  private flags: FlagStore;
  private defaultFlags: FlagStore;
  private subscribers: Set<FlagChangeCallback>;

  constructor(initialFlags: FlagStore | FlagEntry[] = {}) {
    this.flags = {};
    this.defaultFlags = {};
    this.subscribers = new Set();
    
    if (Array.isArray(initialFlags)) {
      for (const flag of initialFlags) {
        this.flags[flag.name] = flag.value;
        this.defaultFlags[flag.name] = flag.value;
      }
    } else {
      this.flags = { ...initialFlags };
      this.defaultFlags = { ...initialFlags };
    }
  }

  /**
   * Load flags from a source (API or JS/TS file)
   */
  async loadFrom(source: FlagSource, options: FlagLoadOptions = {}): Promise<void> {
    const { merge = true, updateDefaults = false } = options;
    const loadedFlags = await source.loadFlags();
    
    const newFlags: FlagStore = Array.isArray(loadedFlags) 
      ? loadedFlags.reduce((acc, flag) => {
          acc[flag.name] = flag.value;
          return acc;
        }, {} as FlagStore)
      : loadedFlags;

    if (merge) {
      this.flags = { ...this.flags, ...newFlags };
      if (updateDefaults) {
        this.defaultFlags = { ...this.defaultFlags, ...newFlags };
      }
    } else {
      this.flags = { ...newFlags };
      if (updateDefaults) {
        this.defaultFlags = { ...newFlags };
      }
    }

    Object.entries(newFlags).forEach(([flagName, value]) => {
      this.notifySubscribers(flagName, value);
    });
  }

  /**
   * Check if a feature flag is enabled
   */
  isEnabled(flagName: string): boolean {
    return Boolean(this.flags[flagName]);
  }

  /**
   * Enable a feature flag
   */
  enable(flagName: string): void {
    this.setFlag(flagName, true);
  }

  /**
   * Disable a feature flag
   */
  disable(flagName: string): void {
    this.setFlag(flagName, false);
  }

  /**
   * Set a feature flag's value
   */
  setFlag(flagName: string, value: FlagValue): void {
    const oldValue = this.flags[flagName];
    this.flags[flagName] = value;
    
    if (oldValue !== value) {
      this.notifySubscribers(flagName, value);
    }
  }

  /**
   * Reset all flags to their default values
   */
  reset(): void {
    this.flags = { ...this.defaultFlags };
    Object.entries(this.flags).forEach(([flagName, value]) => {
      this.notifySubscribers(flagName, value);
    });
  }

  /**
   * Subscribe to flag changes
   */
  subscribe(callback: FlagChangeCallback): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Get all current flag values
   */
  getAllFlags(): FlagStore {
    return { ...this.flags };
  }

  private notifySubscribers(flagName: string, value: FlagValue): void {
    this.subscribers.forEach(callback => {
      callback(flagName, value);
    });
  }
} 